'use client';
/**
 * ⑭ 통합 — 자리(slot) 본문: R14b 전역 지역 칩·전역 검색. 데이터는 NavDataProvider(ClientShell)에서.
 * 서버 HTML 은 칩 「전국」·검색 입력/아이콘 그대로(방문자 무관). 기억·경로 반영은 컴포넌트가 수화 뒤에만 한다.
 */
import React from 'react'; // vitest(클래식 JSX) 렌더 검증용
import { useCallback, useEffect, useState, type ComponentType } from 'react';
import GlobalRegionChip from './GlobalRegionChip';
import type { GlobalSearchProps } from './GlobalSearch';
import { SearchShellDesktop, SearchShellMobile, type SearchArm } from './NavSearchShell';
import { useNavData } from './nav-data-context';
import { currentScope, readRegionMemory } from '@/lib/nav/region-scope';
import { popularArgs } from '@/lib/search/pop-region';
import { fetchPopularRegion } from '@/lib/search/use-pop-region';
import type { NavSlotSurface } from './nav-slots';

export function RegionSlotBody({ surface }: { surface: NavSlotSurface }) {
  const { headerRegion, navV2 } = useNavData();
  // Node 12:00 ① — 킬 스위치 OFF 면 쪽 머리(pagehead)의 지역 칩도 그리지 않는다(구 탐색과 같은 화면).
  if (!navV2) return null;
  return <GlobalRegionChip data={headerRegion} variant={surface === 'mast' ? 'masthead' : 'pagehead'} />;
}

/** R14perf — GlobalSearch 모듈은 첫 수화 경로 밖(별도 청크). 유휴 시간에 미리 받고, 첫 상호작용에서 넘겨준다. */
const loadGlobalSearch = () => import('./GlobalSearch');

/**
 * 마스트 검색: 1024 이상 = 입력창(desktop), 그 아래 = 아이콘→시트(mobile). 둘 다 서버 HTML 에 있고 CSS 로만 가른다.
 * R14perf — 서버 HTML·첫 수화는 가벼운 자리(NavSearchShell, GlobalSearch 닫힌 상태와 같은 마크업).
 *   입력창 포커스·아이콘 누름 → GlobalSearch 로 교체(autoOpen · 친 글자 인계). 기능·계측은 GlobalSearch 그대로.
 */
export function SearchSlotBody() {
  const { headerRegion, popular, navV2, popularOn } = useNavData();
  const [Full, setFull] = useState<ComponentType<GlobalSearchProps> | null>(null);
  const [armed, setArmed] = useState<{ v: 'desktop' | 'mobile'; q: string } | null>(null);

  useEffect(() => {
    if (!navV2) return;
    const pre = () => {
      loadGlobalSearch().catch(() => { /* 첫 상호작용에서 다시 시도 */ });
      // #4g.3 — 「{지역} 인기」 목록도 유휴 시간에 미리(플래그 on 일 때만) — 드롭다운이 열리자마자 2열이 선다.
      if (popularOn) {
        try {
          const a = popularArgs(currentScope(headerRegion, window.location.pathname, window.location.search, readRegionMemory()).node);
          void fetchPopularRegion(a.region, a.sigungu);
        } catch { /* 열릴 때 받는다 */ }
      }
    };
    const ric = typeof window.requestIdleCallback === 'function'; // Safari 는 없다 → setTimeout
    const h = ric ? window.requestIdleCallback(pre, { timeout: 4000 }) : window.setTimeout(pre, 2000);
    return () => { if (ric) window.cancelIdleCallback(h); else clearTimeout(h); };
  }, [navV2, popularOn, headerRegion]);

  const arm = useCallback<SearchArm>((v, input) => {
    loadGlobalSearch().then((m) => {
      // 불러오는 사이 자리 입력창에 친 글자까지 넘긴다.
      setArmed((cur) => cur ?? { v, q: input?.value ?? '' });
      setFull(() => m.default);
    }).catch(() => { /* 네트워크 실패 — 자리는 그대로 두고 다음 상호작용에서 재시도 */ });
  }, []);

  if (!navV2) return null;
  if (!Full || !armed) {
    return (
      <>
        <SearchShellDesktop onArm={arm} className="hidden lg:block" />
        <SearchShellMobile onArm={arm} className="lg:hidden" />
      </>
    );
  }
  return (
    <>
      <Full variant="desktop" data={headerRegion} popular={popular} className="hidden lg:block" autoOpen={armed.v === 'desktop'} initialQ={armed.v === 'desktop' ? armed.q : ''} />
      <Full variant="mobile" data={headerRegion} popular={popular} className="lg:hidden" autoOpen={armed.v === 'mobile'} />
    </>
  );
}
